import { TicketSymbol } from "@lib/titan2-slingo/dist/game/view/abstract/display/TicketSymbol";
import { SpineAnimation } from "@lib/titan2-slingo/dist/game/view/abstract/style/SpineAnimation";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { GlowFilter } from "pixi-filters";
import { Container, Point } from "pixi.js";
import { GameEvent } from "../../../controller/event/GameEvent";
import { GameSoundEvent } from "../../../controller/event/GameSoundEvent";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameSymbolUtil } from "../../../utils/GameSymbolUtil";
import { TitanSpineTrailModifier } from "../../../utils/SpineTrailModifier";
import { zip } from "../../../utils/zip";
import { GameStyle } from "../style/GameStyle";
import { GameReel } from "./GameReel";
/**
* GameTicketSymbol.ts
*/
export class GameTicketSymbol extends TicketSymbol {
    constructor() {
        super(...arguments);
        this.pulsing = false;
        this.dabPromise = Promise.resolve();
    }
    create() {
        super.create();
        MouseUtil.setInteractive(this.button, false);
    }
    createStates() {
        super.createStates();
        // Create a container to contain each possible dab trail size with one glow filter.
        this.dabTrailContainer = new Container();
        this.dabTrailFilter = new GlowFilter({
            distance: this.uiProperties.dabTrailGlowDistance,
            outerStrength: this.uiProperties.dabTrailGlowStrength,
        });
        this.dabTrailContainer.filters = [this.dabTrailFilter];
        // Create the dab trail spines for each size and their modifiers.
        this.dabTrailSpines = zip(this.landscapeProperties.dabTrailSpineProperties, this.portraitProperties?.dabTrailSpineProperties)
            .map(([landscape, portrait]) => TitanSpine.createSpine(this.dabTrailContainer, landscape, portrait));
        this.dabTrailModifiers = this.dabTrailSpines.map(spine => new TitanSpineTrailModifier(spine));
        // Add a distinct overlay spine on top, removing the default one (which was just a duplicate of the symbol spine).
        this.container.removeChild(this.overlaySpine);
        this.overlaySpine = TitanSpine.createSpine(this.container, this.landscapeProperties.dabSpineProperties, this.portraitProperties?.dabSpineProperties);
    }
    destroy() {
        super.destroy();
        if (!this.dabTrailContainer.destroyed) {
            this.dabTrailContainer.destroy({ children: true });
        }
    }
    getWidth() {
        // Return the panel width for correct panel arrangement (not the empty button width).
        return this.getPanelWidth();
    }
    getHeight() {
        // Return the panel height for correct panel arrangement (not the empty button height).
        return this.getPanelHeight();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(GameEvent.NEXT_GAME_TRANSITION, this.handleNextGameTransition);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.unregisterGame(GameEvent.NEXT_GAME_TRANSITION);
    }
    async handleNextGameTransition() {
        const row = this.data.getRow();
        const column = this.data.getColumn();
        // Wait for a delay based on the position of the symbol in the ticket matrix.
        const delay = (column + row) * this.uiProperties.transitionStagger;
        await new Promise(resolve => this.callAfter(delay, resolve));
        // Fade out to hide the current symbol.
        await Promise.all([
            new Promise(resolve => SpineUtil.play(this.spine, "fade-out", resolve)),
            gsap.to(this.spine, { alpha: 1, duration: 0.5 }),
            gsap.to(this.overlaySpine, { alpha: 0, duration: 0.5 }),
        ]);
        // Switch to the new symbol value and update the spine.
        const nextSymbolValue = this.model.getTicket().getCellAt(row, column);
        this.update(nextSymbolValue);
        // Fade in with the new value.
        await new Promise(resolve => {
            SpineUtil.play(this.spine, "fade-in", resolve);
            GameSpineUtil.forceUpdate(this.spine);
        });
        this.overlaySpine.visible = false;
        this.overlaySpine.alpha = 1;
    }
    updateSymbol() {
        GameSymbolUtil.setNumberSkin(this.spine, this.data.getNumber());
        GameSpineUtil.forceUpdate(this.spine);
    }
    dab() {
        this.playDab(true);
    }
    dabSuccess() {
        this.playDab(false);
    }
    async playDab(instant) {
        this.model.setTicketDabInProgress(this);
        this.dabbing = true;
        this.bringToTop();
        this.dabPromise = instant ? this.dabInstantly() : this.dabWithTrail();
        await this.dabPromise;
        // Hide the spine and fade it out so that it can be faded back in without its variant on transition.
        GameSymbolUtil.setColumnSkin(this.spine, this.data.getColumn());
        this.spine.alpha = 0;
        this.model.setTicketDabComplete(this);
    }
    async dabInstantly() {
        await Promise.all([
            new Promise(resolve => SpineUtil.play(this.spine, SpineAnimation.DAB, resolve)),
            new Promise(resolve => SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, resolve)),
        ]);
    }
    async dabWithTrail() {
        // Start at the triggering reel.
        const startColumn = this.model.getLatestMatchedSymbolColumn();
        const startReelId = GameReel.getTrackingId(startColumn);
        const startLocation = this.locationManager.getLocation(startReelId);
        const start = this.toLocal(startLocation.position);
        // End at this symbol.
        const endColumn = this.data.getColumn();
        const endRow = this.data.getRow();
        const end = new Point(0, 0);
        // Determine the difference across the grid.
        const columnDiff = endColumn - startColumn;
        const rowDiff = this.model.getTicket().getNumberOfRows() - endRow;
        // Translate that difference into a curve midpoint for the trail to bend through.
        const outwardsScale = Math.sign(columnDiff) * 0.1;
        const outwardsOffset = new Point(end.y * outwardsScale, -end.x * outwardsScale);
        const midpoint = new Point(end.x / 2 + outwardsOffset.x, end.y / 2 + outwardsOffset.y);
        // To avoid excessive scale differences, there are different spines of different sizes.
        const size = Math.floor(Math.max(Math.abs(columnDiff), rowDiff) / 2);
        const dabTrailSpine = this.dabTrailSpines[size];
        const dabTrailModifier = this.dabTrailModifiers[size];
        console.log(`Dab trail spans ${rowDiff} rows and ${columnDiff} columns, using animation size ${size}.`);
        // Set the glow colour based on the symbol's column.
        this.dabTrailFilter.color = this.uiProperties.dabTrailColours[endColumn];
        // Add the dab trail spine to the container, below the overlay spine, now that it is in use.
        this.container.addChild(this.dabTrailContainer, this.overlaySpine);
        // Play the dab trail aligned between the start and end, and brace for impact.
        SpineUtil.play(dabTrailSpine, "trail");
        dabTrailModifier.alignToPath([start, midpoint, end], 2);
        this.dispatchGame(GameSoundEvent.DAB_SWOOSH);
        await new Promise(resolve => SpineUtil.addNamedEventListener(dabTrailSpine, "impact", resolve));
        // Play the spine dabs together.
        await Promise.all([
            new Promise(resolve => SpineUtil.play(this.spine, SpineAnimation.DAB, resolve)),
            new Promise(resolve => SpineUtil.play(this.overlaySpine, SpineAnimation.DAB, resolve)),
        ]);
        // Remove the dab trail container now that the trail is finished for optimisation.
        this.container.removeChild(this.dabTrailContainer);
    }
    async waitForDabInProgress() {
        return this.dabPromise;
    }
    win() {
        if (this.data.isDabbed()) {
            SpineUtil.play(this.overlaySpine, SpineAnimation.FULL_HOUSE);
        }
        else {
            this.dabSuccess();
        }
    }
    showWin() {
        if (!this.data.isDabbed()) {
            SpineUtil.play(this.spine, SpineAnimation.AVAILABLE_WIN);
        }
    }
    pulseStart() {
        this.pulsing = true;
    }
    pulseEnd() {
        if (this.pulsing) {
            this.pulsing = false;
            if (!this.dabbing) {
                SpineUtil.play(this.spine, "collect");
            }
        }
    }
    select() {
        this.handleButtonSelected();
    }
    unhighlight() {
        this.removeHighlight();
    }
    highlight() {
        SpineUtil.play(this.spine, this.getHighlightAnimation());
        this.removeSymbols();
        this.addSymbol(this.symbolHighlight);
        this.pulseStart();
    }
    highlightSuperWild() {
        SpineUtil.play(this.spine, this.getHighlightAnimation());
        this.removeSymbols();
        this.addSymbol(this.symbolHighlightSuperWild);
        this.pulseStart();
    }
    getHighlightAnimation() {
        return `highlight-${this.data.getColumn()}`;
    }
    static getTrackingId(column, row) {
        return `${GameStyle.TICKET_SYMBOL}_${column}_${row}`;
    }
}
//# sourceMappingURL=GameTicketSymbol.js.map